import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { DEVICES, IV_VIEW_TYPES, ROTATE_TYPES, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { combineLatest, of, Subscription, take } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { EngineService } from '@core/services/engine/engine.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { ModalService } from '@core/services/modal/modal.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SelectBackgroundModalComponent } from '@shared/modals/select-background-modal/select-background-modal.component';
import { QuizModalComponent } from '@shared/modals/quiz-modal/quiz-modal.component';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { BackgroundService } from '@core/services/background-service/background.service';
import { RotateDeviceModalComponent } from '@shared/modals/rotate-device-modal/rotate-device-modal.component';
import { AnotherDeviceModalComponent } from '@shared/modals/another-device-modal/another-device-modal.component';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';

import * as _ from 'lodash';
import { Location } from '@angular/common';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_VIEW_EVENTS } from '@core/services/gtm/gtm.events';
import { QuizService } from '@core/services/quiz/quiz.service';
import { VariantService } from '@core/services/variant/variant.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { first } from 'rxjs/operators';
import { LoaderService } from '@core/services/loader/loader.service';
import { getScreenOrientation } from '@core/helpers/orientation.utils';

@Component({
	selector: 'app-view-switcher',
	templateUrl: './view-switcher.component.html',
	styleUrls: ['./view-switcher.component.scss', './view-switcher.responsive.scss'],
})
export class ViewSwitcherComponent implements OnInit, OnDestroy {
	@Input() prodStatus: boolean;

	getModelLoaded: Subscription;
	getSessionBlindsData: Subscription;
	getViewHandler: Subscription;
	viewTypeSub: Subscription;

	isDesignYourBlind = true;
	hasBlinds = false;
	viewType: string;
	viewTypes = VIEW_TYPES;
	isSampleProject = null;
	isMobile: boolean;

	constructor(
		private gtm: GtmService,
		private location: Location,
		private matDialog: MatDialog,
		private variant: VariantService,
		private quizService: QuizService,
		private shareService: ShareService,
		private modalService: ModalService,
		private engineService: EngineService,
		private passDataService: PassDataService,
		private changeDetection: ChangeDetectorRef,
		private indexedDBService: IndexedDBService,
		private backgroundService: BackgroundService,
		public saveSessionBlind: SaveSessionBlindsService,
		private sessionStorageService: SessionStorageService,
		private destroyRef: DestroyRef,
		private loaderService: LoaderService,
	) {}

	ngOnInit(): void {
		this.isMobile = this.passDataService.isMobileOrTablet;
		this.viewSwitcherHandler();
		this.initViewTypeOnSceneReady();
	}

	ngOnDestroy() {
		this.getModelLoaded.unsubscribe();
		this.getSessionBlindsData.unsubscribe();
		this.getViewHandler.unsubscribe();
		if (this.viewTypeSub) {
			this.viewTypeSub.unsubscribe();
		}
	}

	viewSwitcherHandler() {
		this.loaderService.setLoading(true);

		this.getModelLoaded = this.shareService.getSceneCreated.subscribe(() => {
			this.hasBlinds = !_.isEmpty(this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data));
			this.setCurrentBlindDesignSettings();
			this.modelViewerLoaderHandler(false);
		});
		this.getSessionBlindsData = this.shareService.getSessionBlindsData.subscribe((res) => (this.hasBlinds = res));
		this.sessionStorageService.setBlindData(
			this.isDesignYourBlind ? VIEW_TYPES.design : VIEW_TYPES.image_visualisation,
			STORAGE_NAMES.zip_view_type,
		);

		this.getViewHandler = this.shareService.getViewType.subscribe(this.onViewChange.bind(this));
		this.matDialog.afterOpened.subscribe((dialog) => {
			if (dialog.id === QuizModalComponent.ID) {
				this.isSampleProject = !!this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background)?.type;
			}
		});
	}

	initViewTypeOnSceneReady(): void {
		this.shareService.getSceneCreated.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.imageVisualisationModeHandler();
		});
	}

	imageVisualisationModeHandler() {
		// Called when navigating from Overview page or when reloading the page.
		this.getReadyToSwitchObservable().subscribe(() => {
			const isImageVisualisation = this.sessionStorageService.getSession(STORAGE_NAMES.zip_image_visualisation_status);

			if (!isImageVisualisation) {
				this.loaderService.setLoading(false);

				if (!this.viewTypeSub) {
					this.viewTypeSub = combineLatest([this.shareService.getAddBlind, this.shareService.getAccordionType])
						.pipe(first())
						.subscribe(this.shareService.setViewType.bind(this.shareService, this.viewTypes.design));
				}
				return;
			}

			setTimeout(this.onSwitchViewMode.bind(this, this.viewTypes.image_visualisation));
		});
	}

	getReadyToSwitchObservable() {
		const sessionId = this.location.path().split('?=')[1];

		return sessionId
			? combineLatest([
					this.shareService.getSessionBlindsData, // Waiting for Blind Session Data
					this.shareService.getBlindSelect, // Waiting for selected Blind due to Interior Blind loading
				]).pipe(first())
			: of(null);
	}

	onSwitchViewMode(type: string) {
		const toggleType = () => (this.isDesignYourBlind = type === this.viewTypes.design);
		const ivBackgroundImage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
		const sessionSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);
		const ivSceneSetting = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config).iv_scene_settings;
		const currentDevice = this.engineService.mobileAndTabletCheck() ? DEVICES.mobile : DEVICES.desktop;

		if (sessionSaved && ivBackgroundImage && ivSceneSetting && currentDevice !== ivSceneSetting.device) {
			const dialogRef = this.modalService.openResponsiveDialog({
				component: AnotherDeviceModalComponent,
				...this.modalService.getConfig(AnotherDeviceModalComponent.ID),
			});

			dialogRef.afterOpened().subscribe(() => this.loaderService.setLoading(false));
			dialogRef.afterClosed().subscribe((continueStatus) => {
				if (continueStatus) {
					this.onSwitchViewMode(type);
				}
			});

			return;
		}

		if (type === this.viewTypes.design || !ivBackgroundImage) {
			toggleType();
		}

		if (type !== this.viewTypes.design) {
			if (!ivBackgroundImage) {
				this.loaderService.setLoading(false);
				this.matDialog
					.open(SelectBackgroundModalComponent, {
						...this.modalService.getConfig(SelectBackgroundModalComponent.ID),
						data: { context: 'view_switcher' },
					})
					.afterClosed()
					.subscribe((continueStatus) => {
						if (continueStatus) {
							this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
						} else {
							this.isDesignYourBlind = true;
						}
					});
			} else {
				const isUploadImage = ivBackgroundImage.id.includes('own');
				const currentIVType = isUploadImage ? 'upload' : 'project_sample';
				const currentBackgroundImageId = isUploadImage
					? ivBackgroundImage.id
					: ivBackgroundImage.id.split('_').slice(0, -1).join('_') + `_${getScreenOrientation()}`;

				this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, currentBackgroundImageId).then((image) => {
					const switchViewMode = (currentImage) => {
						this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
						this.sessionStorageService.setBlindData(currentIVType, STORAGE_NAMES.zip_iv_type);
						this.engineService.setBackgroundImage(currentImage);
						this.passDataService.isLiveOpened = false;

						this.shareService.getViewType.pipe(first()).subscribe(toggleType);
						setTimeout(this.loaderService.setLoading.bind(this.loaderService, false));
						this.quizService.showQuizIfNeeded(currentIVType);
					};

					if (image) {
						image = {
							...image,
							theme: ivBackgroundImage.theme,
							settings: ivBackgroundImage.settings,
							shutterValues: ivBackgroundImage.shutterValues,
							sampleProjectBlinds: ivBackgroundImage.sampleProjectBlinds,
						};

						switchViewMode(image);
					} else {
						this.backgroundService.getSampleProjectImage(currentBackgroundImageId).then(switchViewMode);
					}
				});
			}
		} else {
			const sessionSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);
			const changeViewType = () => {
				this.sessionStorageService.setSession(IV_VIEW_TYPES.no_background, STORAGE_NAMES.zip_iv_type);
				this.sessionStorageService.setSession(type, STORAGE_NAMES.zip_view_type);
				this.engineService.setBackgroundImage(null, true);
				this.modelViewerLoaderHandler(true);
				this.shareService.setViewType(this.viewTypes.design);
				this.quizService.showQuizIfNeeded(VIEW_TYPES.design);
			};

			this.engineService.setModelAndBoundingBoxSettings();
			this.engineService.setIVSceneScreenShots().then(() => {
				this.engineService.saveModelAndBoundingBoxSettings(false);

				if (sessionSaved) {
					this.saveSessionBlind.PutStorageDataToServerPromise().then(() => changeViewType());
				} else {
					changeViewType();
				}
			});
		}

		this.gtm.pushEvent(GTM_VIEW_EVENTS[type.toUpperCase()]);
	}

	setCurrentBlindDesignSettings(): void {
		const id = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);
		const type = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_type);

		if (id && type) {
			this.shareService.setBlindSelect({ id, type });
		}
	}

	onViewChange(type: string): void {
		this.sessionStorageService.setBlindData(type, STORAGE_NAMES.zip_view_type);

		setTimeout(() => {
			this.isDesignYourBlind = type === VIEW_TYPES.design;
			this.passDataService.isLiveOpened = this.isDesignYourBlind ? false : this.passDataService.isLiveOpened;

			if (this.isDesignYourBlind) {
				this.engineService.closeVideoStream();
			} else {
				this.openRotateDeviceModal();
			}

			this.changeDetection.markForCheck();
		});

		this.viewType = type;
	}

	modelViewerLoaderHandler(status: boolean): void {
		document.querySelector(SELECTORS.model_viewer).classList.toggle('loader', status);
	}

	openRotateDeviceModal(): void {
		const isOpen = this.matDialog.getDialogById(RotateDeviceModalComponent.ID);
		const isPhoneLandscape = getScreenOrientation() === 'landscape' && window.innerHeight <= 500 && window.innerWidth > window.innerHeight;
		const isSampleProject = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type) === 'project_sample';

		if (!isOpen && this.isMobile && isPhoneLandscape) {
			this.matDialog.open(
				RotateDeviceModalComponent,
				this.modalService.getConfig(RotateDeviceModalComponent.ID, { rotateType: ROTATE_TYPES.to_portrait, isSampleProject }),
			);
		}
	}
}
